import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { AppSessionService } from "../../app-session/app-session.service";

import { ProductSupplierDTO } from "src/app/models/Products/productSupplierDTO";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import { setAdvancedFilterQuery, setSimpleFilterQuery } from "src/app/shared/utils/tableUtils";
import { GridUtils } from "src/app/shared/utils/gridUtils";

const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class ProductSuppliersService {
  constructor(
    private http: HttpClient,
    private appSession: AppSessionService
  ) {}
    
  private PRODUCT_SUPPLIERS_URL = this.appSession.baseUrl + "/api/v1/productSuppliers";

  getProductSupplier(productCode: string, partySupplierCode: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.PRODUCT_SUPPLIERS_URL}/byProduct/${productCode}/bySupplier/${partySupplierCode}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getProductSuppliersList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    const url = paging
      ? this.buildDataUrl(paging, "/search?")
      : `${this.PRODUCT_SUPPLIERS_URL}/search`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  createProductSupplier(productSupplier: ProductSupplierDTO): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(productSupplier);
    const url = `${this.PRODUCT_SUPPLIERS_URL}`;
    return this.http.post<any>(url, data, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  updateProductSupplier(productSupplier: ProductSupplierDTO): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const data = JSON.stringify(productSupplier);
    const url = `${this.PRODUCT_SUPPLIERS_URL}`;
    return this.http.put<any>(url, data, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  deleteProductSupplier(productCode: string, partySupplierCode: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.PRODUCT_SUPPLIERS_URL}/byProduct/${productCode}/bySupplier/${partySupplierCode}`;
    return this.http.delete<any>(url, { headers }).pipe(
      catchError((error) => throwError(error))
    );
  }

  // Grid filter helpers block
  private buildDataUrl(pagingArgs: any, path: string): string {
    let baseQueryString = `${this.PRODUCT_SUPPLIERS_URL}${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query += scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;
    baseQueryString += query;

    return baseQueryString;
  }
}
